import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import BuildInfo from './components/BuildInfo';
import './i18n';
import './design/fonts.css';
import './design/tokens.css';
import './design/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// A page (or the service worker's precache) left open across a deploy can
// still hold a reference to a hashed chunk filename that no longer exists
// on the server — a fresh build replaces every hashed asset. Navigating
// into a lazy route then 404s the dynamic import() and crashes the whole
// app instead of just that navigation. Vite fires this event specifically
// for that case; reloading fetches a current index.html (and lets an
// already-installed newer service worker take over). Guard against a
// reload loop in case something else is actually broken.
window.addEventListener('vite:preloadError', () => {
  const key = 'nr-preload-reload-at';
  const last = Number(sessionStorage.getItem(key) ?? 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
});

// The browser only checks a registered service worker for updates lazily
// (on navigation, throttled to roughly once/24h for the worker script's own
// bytes) — on iOS this has left a stale worker serving an old app shell
// long after a deploy moved on (see the ios-pwa-cache-staleness memory).
// Nudge a check whenever the tab regains visibility, which is exactly when
// a reopened-after-a-while PWA/tab matters most.
if ('serviceWorker' in navigator) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    void navigator.serviceWorker.getRegistration().then((reg) => reg?.update());
  });
}

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');
ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      <BuildInfo />
    </QueryClientProvider>
  </React.StrictMode>,
);
